/**
 * Catalogue sizes for testing the contracts list — a prototype-only dial.
 *
 * The sizes are the ones the two list variants turn on: a catalogue that fits a
 * screenful, one smaller than a single load, one that needs a load and a part.
 * The number is the size of the **catalogue**, not of the Eligible tab: the
 * chosen scenario still decides how it splits between Eligible and Skipped, so
 * `Many excluded` at 135 is a large catalogue with many exclusions.
 */
export interface ContractCountOption {
  /** null = leave the scenario's own catalogue alone. */
  count: number | null;
  label: string;
  description: string;
}

export const CONTRACT_COUNTS: ContractCountOption[] = [
  {
    count: null,
    label: "Scenario default",
    description: "The catalogue the chosen scenario seeds, at its own size.",
  },
  { count: 6, label: "6 contracts", description: "Fewer rows than one screenful holds." },
  { count: 10, label: "10 contracts", description: "About one screenful, on most windows." },
  { count: 48, label: "48 contracts", description: "Several pages; less than one full load." },
  { count: 60, label: "60 contracts", description: "One full load, and a little over." },
  { count: 100, label: "100 contracts", description: "Several pages; two loads." },
  { count: 135, label: "135 contracts", description: "Two full loads and a partial one." },
];

const KEY = "dev-mock-brs-contract-count";

export function activeContractCount(): number | null {
  try {
    const stored = localStorage.getItem(KEY);
    if (!stored) return null;
    const parsed = Number.parseInt(stored, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function activeContractCountOption(): ContractCountOption {
  const count = activeContractCount();
  return CONTRACT_COUNTS.find((o) => o.count === count) ?? CONTRACT_COUNTS[0];
}

/** Stores the choice. The caller reloads — the mock seeds at module load. */
export function storeContractCount(count: number | null): void {
  if (count === null) localStorage.removeItem(KEY);
  else localStorage.setItem(KEY, String(count));
}
